/**
 * DynamicFieldRenderer v2 — Advanced field types + validation engine
 * 
 * GLOBAL COMPONENT — used by ALL master/transaction forms.
 * Supports: text, number, date, dropdown, checkbox, textarea,
 *           email, phone, multi-email, multi-phone, image, file
 * Validation: required, regex, min/max, email format, phone format
 * Access: Add Field restricted to admin users
 * 
 * NO code change needed to add new fields — just insert row in DB.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Minus, X, Upload, Paperclip, Trash2 } from 'lucide-react';
import '../../styles/erp-theme.css';
import { API_BASE as API_URL } from '../../lib/api';


// Validation helpers
const validateEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const validatePhone = (v) => /^[\d\s+\-()]{7,15}$/.test(v);

const DynamicFieldRenderer = ({ screenName, recordId, token, values, onChange, readOnly = false, userRole }) => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [errors, setErrors] = useState({});
  const [showAddField, setShowAddField] = useState(false);
  const [newField, setNewField] = useState({ FieldKey: '', FieldLabel: '', FieldType: 'text', DropdownOptions: '', IsRequired: false, SectionName: 'Additional Fields', RegexPattern: '', MinValue: '', MaxValue: '', PlaceholderText: '', IsMultiValue: false });

  const isAdmin = userRole === 'admin' || userRole === 'SYS_SUPERUSER' || userRole === 'SUPER_ADMIN'
    || userRole === 'LICENSOR' || userRole === 'TENANT_ADMIN' || userRole === 'ADMINSUPPORT';

  const loadFields = useCallback(async () => {
    if (!token || !screenName) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/dynamic-fields/config?screen=${screenName}`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      if (json.success) setFields(json.data || []);
    } catch (e) { console.error('Dynamic fields load failed:', e); }
    setLoading(false);
  }, [token, screenName]);

  useEffect(() => { loadFields(); }, [loadFields]);

  useEffect(() => {
    if (!token || !screenName || !recordId) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/dynamic-fields/values?screen=${screenName}&recordId=${recordId}`, { headers: { Authorization: `Bearer ${token}` } });
        const json = await res.json();
        if (json.success && json.data) {
          Object.entries(json.data).forEach(([key, val]) => onChange?.(key, val));
        }
      } catch {}
    })();
  }, [token, screenName, recordId]);

  const handleAddField = async () => {
    if (!newField.FieldKey || !newField.FieldLabel) return;
    try {
      const res = await fetch(`${API_URL}/api/dynamic-fields/config`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ModuleName: '', SubModuleName: '', ScreenName: screenName, ...newField, DisplayOrder: fields.length + 1 })
      });
      const json = await res.json();
      if (json.success) { setShowAddField(false); setNewField({ FieldKey: '', FieldLabel: '', FieldType: 'text', DropdownOptions: '', IsRequired: false, SectionName: 'Additional Fields', RegexPattern: '', MinValue: '', MaxValue: '', PlaceholderText: '', IsMultiValue: false }); loadFields(); }
    } catch {}
  };

  const handleDeleteField = async (fieldId, fieldLabel) => {
    if (!window.confirm(`Remove field "${fieldLabel}"? This will delete the field configuration.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/dynamic-fields/config/${fieldId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      if (json.success) loadFields();
    } catch {}
  };

  // Validate a single field
  const validateField = (field, val) => {
    if (field.IsRequired && (!val || val === '' || val === '[]')) return `${field.FieldLabel} is required`;
    if (!val || val === '') return null;
    if (field.RegexPattern) { try { if (!new RegExp(field.RegexPattern).test(val)) return `Invalid format`; } catch {} }
    if (field.MinValue && Number(val) < Number(field.MinValue)) return `Minimum: ${field.MinValue}`;
    if (field.MaxValue && Number(val) > Number(field.MaxValue)) return `Maximum: ${field.MaxValue}`;
    if (field.FieldType === 'email' && !validateEmail(val)) return 'Invalid email';
    if (field.FieldType === 'phone' && !validatePhone(val)) return 'Invalid phone';
    return null;
  };

  // Validate all and return errors
  const validateAll = () => {
    const errs = {};
    fields.forEach(f => {
      const err = validateField(f, values?.[f.FieldKey]);
      if (err) errs[f.FieldKey] = err;
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Expose validate via ref-like pattern
  useEffect(() => {
    if (window._erpDynamicValidators) window._erpDynamicValidators[screenName] = validateAll;
    else window._erpDynamicValidators = { [screenName]: validateAll };
  });

  if (fields.length === 0 && !loading && !showAddField && !isAdmin) return null;

  const sections = {};
  fields.forEach(f => {
    const sec = f.SectionName || 'Additional Fields';
    if (!sections[sec]) sections[sec] = [];
    sections[sec].push(f);
  });

  // Multi-value helpers
  const getMultiValues = (key) => { try { return JSON.parse(values?.[key] || '[]'); } catch { return []; } };
  const setMultiValues = (key, arr) => onChange?.(key, JSON.stringify(arr));

  const renderField = (field) => {
    const val = values?.[field.FieldKey] ?? field.DefaultValue ?? '';
    const handleChange = (v) => { onChange?.(field.FieldKey, v); if (errors[field.FieldKey]) setErrors(e => { const n = {...e}; delete n[field.FieldKey]; return n; }); };
    const ph = field.PlaceholderText || '';
    const err = errors[field.FieldKey];

    switch (field.FieldType) {
      case 'text':
        return <><input className={`erp-input${err ? ' erp-input-error' : ''}`} value={val} onChange={(e) => handleChange(e.target.value)} disabled={readOnly} placeholder={ph} data-testid={`dyn-${field.FieldKey}`} />{err && <span className="erp-field-error">{err}</span>}</>;

      case 'number':
        return <><input className={`erp-input${err ? ' erp-input-error' : ''}`} type="number" value={val} onChange={(e) => handleChange(e.target.value)} disabled={readOnly} placeholder={ph} min={field.MinValue || undefined} max={field.MaxValue || undefined} data-testid={`dyn-${field.FieldKey}`} />{err && <span className="erp-field-error">{err}</span>}</>;

      case 'date':
        return <input className="erp-input" type="date" value={val} onChange={(e) => handleChange(e.target.value)} disabled={readOnly} data-testid={`dyn-${field.FieldKey}`} />;

      case 'dropdown': {
        const options = (field.DropdownOptions || '').split(',').filter(Boolean);
        return <select className="erp-input erp-select" value={val} onChange={(e) => handleChange(e.target.value)} disabled={readOnly} data-testid={`dyn-${field.FieldKey}`}><option value="">--Select--</option>{options.map(o => <option key={o.trim()} value={o.trim()}>{o.trim()}</option>)}</select>;
      }

      case 'checkbox':
        return <label className="erp-checkbox" style={{ marginBottom: 0 }}><input type="checkbox" checked={val === 'true' || val === true} onChange={(e) => handleChange(String(e.target.checked))} disabled={readOnly} data-testid={`dyn-${field.FieldKey}`} />{field.FieldLabel}</label>;

      case 'textarea':
        return <textarea className="erp-input" rows={3} value={val} onChange={(e) => handleChange(e.target.value)} disabled={readOnly} placeholder={ph} data-testid={`dyn-${field.FieldKey}`} />;

      case 'email':
        return <><input className={`erp-input${err ? ' erp-input-error' : ''}`} type="email" value={val} onChange={(e) => handleChange(e.target.value)} disabled={readOnly} placeholder={ph || 'email@example.com'} data-testid={`dyn-${field.FieldKey}`} />{err && <span className="erp-field-error">{err}</span>}</>;

      case 'phone':
        return <><input className={`erp-input${err ? ' erp-input-error' : ''}`} type="tel" value={val} onChange={(e) => handleChange(e.target.value)} disabled={readOnly} placeholder={ph || '+91 9876543210'} data-testid={`dyn-${field.FieldKey}`} />{err && <span className="erp-field-error">{err}</span>}</>;

      case 'multi-email': {
        const items = getMultiValues(field.FieldKey);
        return (
          <div className="erp-multi-field" data-testid={`dyn-${field.FieldKey}`}>
            {items.map((item, i) => (
              <div key={i} className="erp-multi-field-row">
                <input className={`erp-input${!validateEmail(item) && item ? ' erp-input-error' : ''}`} type="email" value={item}
                  onChange={(e) => { const a = [...items]; a[i] = e.target.value; setMultiValues(field.FieldKey, a); }}
                  disabled={readOnly} placeholder="email@example.com" />
                {!readOnly && <button className="erp-multi-field-remove" onClick={() => { const a = items.filter((_, j) => j !== i); setMultiValues(field.FieldKey, a); }}><X size={12} /></button>}
                {!validateEmail(item) && item && <span className="erp-field-error">Invalid email</span>}
              </div>
            ))}
            {!readOnly && <button className="erp-btn erp-btn-ghost" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => setMultiValues(field.FieldKey, [...items, ''])}><Plus size={11} /> Add Email</button>}
          </div>
        );
      }

      case 'multi-phone': {
        const items = getMultiValues(field.FieldKey);
        return (
          <div className="erp-multi-field" data-testid={`dyn-${field.FieldKey}`}>
            {items.map((item, i) => (
              <div key={i} className="erp-multi-field-row">
                <input className={`erp-input${!validatePhone(item) && item ? ' erp-input-error' : ''}`} type="tel" value={item}
                  onChange={(e) => { const a = [...items]; a[i] = e.target.value; setMultiValues(field.FieldKey, a); }}
                  disabled={readOnly} placeholder="+91 9876543210" />
                {!readOnly && <button className="erp-multi-field-remove" onClick={() => { const a = items.filter((_, j) => j !== i); setMultiValues(field.FieldKey, a); }}><X size={12} /></button>}
                {!validatePhone(item) && item && <span className="erp-field-error">Invalid phone</span>}
              </div>
            ))}
            {!readOnly && <button className="erp-btn erp-btn-ghost" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => setMultiValues(field.FieldKey, [...items, ''])}><Plus size={11} /> Add Phone</button>}
          </div>
        );
      }

      case 'image':
        return (
          <div className="erp-file-field" data-testid={`dyn-${field.FieldKey}`}>
            {val && <img src={val} alt={field.FieldLabel} style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 'var(--erp-radius)', border: '1px solid var(--erp-border)', marginBottom: 4 }} />}
            {!readOnly && <label className="erp-btn erp-btn-ghost" style={{ fontSize: 11, padding: '4px 10px', cursor: 'pointer' }}>
              <Upload size={12} /> Browse
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => {
                const file = e.target.files?.[0]; if (!file) return;
                if (file.size > 5 * 1024 * 1024) { alert('Max 5MB'); return; }
                const reader = new FileReader(); reader.onloadend = () => handleChange(reader.result); reader.readAsDataURL(file);
              }} />
            </label>}
          </div>
        );

      case 'file':
        return (
          <div className="erp-file-field" data-testid={`dyn-${field.FieldKey}`}>
            {val && <span style={{ fontSize: 11, color: 'var(--erp-success)' }}><Paperclip size={11} /> File attached</span>}
            {!readOnly && <label className="erp-btn erp-btn-ghost" style={{ fontSize: 11, padding: '4px 10px', cursor: 'pointer' }}>
              <Upload size={12} /> Upload
              <input type="file" style={{ display: 'none' }} onChange={(e) => {
                const file = e.target.files?.[0]; if (!file) return;
                if (file.size > 10 * 1024 * 1024) { alert('Max 10MB'); return; }
                const reader = new FileReader(); reader.onloadend = () => handleChange(reader.result); reader.readAsDataURL(file);
              }} />
            </label>}
          </div>
        );

      default:
        return <input className="erp-input" value={val} onChange={(e) => handleChange(e.target.value)} disabled={readOnly} placeholder={ph} data-testid={`dyn-${field.FieldKey}`} />;
    }
  };

  return (
    <div className="erp-dynamic-fields" data-testid="dynamic-fields-section">
      {Object.entries(sections).map(([sectionName, sectionFields]) => (
        <div key={sectionName} style={{ marginTop: 'var(--erp-sp-3)' }}>
          <button className="erp-collapse-toggle" onClick={() => setExpanded(!expanded)} data-testid="dynamic-toggle">
            {expanded ? <Minus size={14} /> : <Plus size={14} />} {sectionName}
            <span style={{ marginLeft: 'auto', fontSize: 10, opacity: 0.6 }}>{sectionFields.length} fields</span>
          </button>
          {expanded && (
            <div className="erp-form-2col" style={{ marginTop: 'var(--erp-sp-2)' }}>
              {sectionFields.map(field => (
                field.FieldType === 'checkbox' ? (
                  <div key={field.FieldKey} className="erp-form-row">
                    <div className="erp-form-control" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {renderField(field)}
                      {isAdmin && !readOnly && <button className="erp-multi-field-remove" onClick={() => handleDeleteField(field.Id, field.FieldLabel)} title="Remove field" style={{ width: 18, height: 18 }}><Trash2 size={10} /></button>}
                    </div>
                  </div>
                ) : (
                  <div key={field.FieldKey} className="erp-form-row" style={['multi-email','multi-phone','image','file','textarea'].includes(field.FieldType) ? { gridColumn: '1 / -1' } : undefined}>
                    <label className={`erp-form-label${field.IsRequired ? ' required' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {field.FieldLabel}
                      {isAdmin && !readOnly && <button className="erp-multi-field-remove" onClick={() => handleDeleteField(field.Id, field.FieldLabel)} title="Remove field" style={{ width: 16, height: 16, flexShrink: 0 }}><Trash2 size={9} /></button>}
                    </label>
                    <div className="erp-form-control" style={{ flexDirection: 'column', alignItems: 'stretch' }}>{renderField(field)}</div>
                  </div>
                )
              ))}
            </div>
          )}
        </div>
      ))}

      {loading && <div style={{ fontSize: 11, color: 'var(--erp-text-muted)', padding: 8 }}>Loading dynamic fields...</div>}

      {/* Add Field — admin only */}
      {!readOnly && isAdmin && (
        <div style={{ marginTop: 'var(--erp-sp-2)' }}>
          {!showAddField ? (
            <button className="erp-btn erp-btn-ghost" onClick={() => setShowAddField(true)} data-testid="add-dynamic-field-btn" style={{ fontSize: 11 }}><Plus size={12} /> Add Field</button>
          ) : (
            <div style={{ padding: 'var(--erp-sp-3)', background: 'var(--erp-surface-alt)', borderRadius: 'var(--erp-radius)', border: '1px solid var(--erp-border)', marginTop: 'var(--erp-sp-2)' }}>
              <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 8, color: 'var(--erp-primary)' }}>Add New Dynamic Field</div>
              <div className="erp-form-2col">
                <div className="erp-form-row"><label className="erp-form-label required">Field Key</label><div className="erp-form-control"><input className="erp-input" value={newField.FieldKey} onChange={(e) => setNewField(p => ({...p, FieldKey: e.target.value.replace(/\s/g,'')}))} placeholder="e.g. CustomField1" data-testid="new-field-key" /></div></div>
                <div className="erp-form-row"><label className="erp-form-label required">Label</label><div className="erp-form-control"><input className="erp-input" value={newField.FieldLabel} onChange={(e) => setNewField(p => ({...p, FieldLabel: e.target.value}))} placeholder="e.g. Custom Field" data-testid="new-field-label" /></div></div>
                <div className="erp-form-row"><label className="erp-form-label">Type</label><div className="erp-form-control"><select className="erp-input erp-select" value={newField.FieldType} onChange={(e) => setNewField(p => ({...p, FieldType: e.target.value}))}><option value="text">Text</option><option value="number">Number</option><option value="date">Date</option><option value="dropdown">Dropdown</option><option value="checkbox">Checkbox</option><option value="textarea">Textarea</option><option value="email">Email</option><option value="phone">Phone</option><option value="multi-email">Multi Email</option><option value="multi-phone">Multi Phone</option><option value="image">Image Upload</option><option value="file">File Upload</option></select></div></div>
                <div className="erp-form-row"><label className="erp-form-label">Placeholder</label><div className="erp-form-control"><input className="erp-input" value={newField.PlaceholderText} onChange={(e) => setNewField(p => ({...p, PlaceholderText: e.target.value}))} placeholder="Hint text" /></div></div>
                {newField.FieldType === 'dropdown' && (
                  <div className="erp-form-row" style={{ gridColumn: '1/-1' }}><label className="erp-form-label">Options</label><div className="erp-form-control"><input className="erp-input" value={newField.DropdownOptions} onChange={(e) => setNewField(p => ({...p, DropdownOptions: e.target.value}))} placeholder="Option1,Option2,Option3" /></div></div>
                )}
                {newField.FieldType === 'number' && (<>
                  <div className="erp-form-row"><label className="erp-form-label">Min</label><div className="erp-form-control"><input className="erp-input" type="number" value={newField.MinValue} onChange={(e) => setNewField(p => ({...p, MinValue: e.target.value}))} /></div></div>
                  <div className="erp-form-row"><label className="erp-form-label">Max</label><div className="erp-form-control"><input className="erp-input" type="number" value={newField.MaxValue} onChange={(e) => setNewField(p => ({...p, MaxValue: e.target.value}))} /></div></div>
                </>)}
                <div className="erp-form-row"><label className="erp-form-label">Regex</label><div className="erp-form-control"><input className="erp-input" value={newField.RegexPattern} onChange={(e) => setNewField(p => ({...p, RegexPattern: e.target.value}))} placeholder="^[A-Z]+$" /></div></div>
                <div className="erp-form-row"><label className="erp-form-label">Required</label><div className="erp-form-control"><input type="checkbox" checked={newField.IsRequired} onChange={(e) => setNewField(p => ({...p, IsRequired: e.target.checked}))} /></div></div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button className="erp-btn erp-btn-primary" onClick={handleAddField} style={{ fontSize: 11 }}>Save Field</button>
                <button className="erp-btn erp-btn-ghost" onClick={() => setShowAddField(false)} style={{ fontSize: 11 }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DynamicFieldRenderer;
